import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';

import { dollarFormat, getPropByString, paginationDropdownOptions, toTitleCase } from 'utils/utils';
import { useTrustAccountList } from './hooks/useTrustAccountList';
import { useCaseListFilter as useFilter } from 'modules/cases/hooks/useCaseListFilter';
import { useAccounts } from 'hooks/useAccounts';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { LoaderComponent } from 'shared/LoaderComponent';
import constants from 'constants/index';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';

const OperatingAccounting = ({ caseDetails, caseView, setActiveIndex, temporaryFundsTabIndex }) => {
  const { accountList, isAccountsLoading } = useAccounts({ accountType: 'operating', excludeFromLoading: true });
  const { accountList: trustAccountList, isAccountsLoading: isTrustAccountsLoading } = useAccounts({
    accountType: 'trust',
    excludeFromLoading: true,
  });
  const { userDetails } = useUserDetailsContext();

  const trustOptionswithExternal = [
    { label: 'External', value: 'external_source' },
    ...trustAccountList.map((account) => ({ label: account.account_name, value: account.account_id })),
  ];

  const { filter, filterOnChange, refreshList } = useFilter({
    case_id: caseView ? caseDetails?.case_id : null,
    account_type: 'operating',
  });
  const {
    trustLogs: operatingLogs,
    loadTrustLogs,
    hasNext,
    hasPrevious,
    fetchNext,
    fetchprev,
    onPageChange,
    pageSize,
    loadTrustAccountBalance,
    operatingBalance,
  } = useTrustAccountList({
    _filter: filter,
  });

  useEffect(() => {
    // calling loadTrustLogs with operating account filter type to get operating logs
    loadTrustLogs({ filter });
    if (caseDetails?.default_operating_account?.[0]?.account_id || filter?.account_id) {
      loadTrustAccountBalance(
        filter?.account_id || caseDetails?.default_operating_account?.[0]?.account_id,
        caseView ? caseDetails?.case_id : null,
        'operating'
      );
    }
  }, [filter]);

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: () => {
      return (
        <React.Fragment>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={pageSize} options={paginationDropdownOptions} onChange={onPageChange} />
        </React.Fragment>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchNext} disabled={hasNext} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button onClick={fetchprev} disabled={hasPrevious} className={'p-paginator-next p-paginator-element p-link'} icon={iconClassName} />
      );
    },
  };

  const fromTrustBody = (rowData) => {
    let entry_type = rowData?.entry_type;
    return (
      <>
        <span className="p-column-title text-break">Amount</span>
        <span>
          <span className={entry_type === 'cr' ? 'text-success' : 'text-danger'} title={dollarFormat(rowData?.amount)}>
            {dollarFormat(rowData?.amount)}
          </span>
        </span>
      </>
    );
  };

  const paymentTypeBody = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Payment Type</span>
        <span title={toTitleCase(rowData?.payment_type)}>{toTitleCase(rowData?.payment_type)}</span>
      </>
    );
  };

  const dateBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date of Transaction</span>
        <span
          className="text-break"
          title={
            rowData?.record_created_date
              ? handleDateTimeOffset(userDetails?.timezone, rowData.record_created_date, constants.month_date_year_format)
              : undefined
          }
        >
          {rowData?.record_created_date
            ? handleDateTimeOffset(userDetails?.timezone, rowData.record_created_date, constants.month_date_year_format)
            : '-'}
        </span>
      </React.Fragment>
    );
  };

  const accountBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid To</span>
        {/* <span className="text-break">
          <Link to="/settings/accounts" className="call-back text-decoration-none" title={rowData?.credit_account_name}>
            {rowData?.credit_account_name}
          </Link>
        </span> */}
        {rowData?.credit_account_name ? (
          <Link to="/settings/accounts" className="call-back text-decoration-none" title={rowData?.credit_account_name}>
            {rowData?.credit_account_name}
          </Link>
        ) : (
          <Link
            to={`/contacts/${
              rowData?.provider?.contact_id ||
              rowData?.vendor?.contact_id ||
              rowData?.checked_contact?.contact_id ||
              rowData?.contact_id ||
              rowData?.referral_contact?.contact_id
            }`}
            className="call-back text-decoration-none"
            title={
              rowData?.provider?.title ||
              rowData?.vendor?.title ||
              rowData?.checked_contact?.fullName ||
              rowData?.contact_name ||
              rowData?.referral_contact?.fullName
            }
          >
            {rowData?.provider?.title ||
              rowData?.vendor?.title ||
              rowData?.checked_contact?.fullName ||
              rowData?.contact_name ||
              rowData?.referral_contact?.fullName}
          </Link>
        )}
      </React.Fragment>
    );
  };

  const paidFromBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span className="text-break">
          {rowData?.debit_account_name ? (
            <Link to="/settings/accounts" className="call-back text-decoration-none" title={rowData?.debit_account_name}>
              {rowData?.debit_account_name}
            </Link>
          ) : (
            '-'
          )}
        </span>
      </React.Fragment>
    );
  };

  const caseTemplate = (rowData, ...rest) => {
    const { header, field } = rest?.[0];
    return (
      <React.Fragment>
        <span className="p-column-title text-break">{header}</span>
        <span className="text-break">
          <Link
            className="cursor-pointer text-decoration-none F-size14 call-back"
            to={`/${rowData?.is_lead ? 'leads' : 'cases'}/${rowData?.case_id}`}
            title={getPropByString(rowData, field)}
          >
            {getPropByString(rowData, field)}
          </Link>
        </span>
      </React.Fragment>
    );
  };

  const rowClassName = (rowData) => ({ strikethrough: rowData.is_strikethrough });

  const refreshData = () => {
    refreshList({
      case_id: caseView ? caseDetails?.case_id : null,
      account_type: 'operating',
    });
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-lg-0 p-2 w-100 align-items-center justify-content-between">
        <div className="d-flex flex-wrap align-items-center p-2 filter-wraper">
          {(filter?.account_id || caseView) && (
            <label className="p-0 m-0 filter-label">
              Balance: <span className="text-bold black-600 ">{dollarFormat(operatingBalance)}</span>
            </label>
          )}
        </div>
        <div className="d-flex flex-wrap align-items-center p-2 filter-wraper gap-2">
          <label className="text-bold black-600 p-0 m-0 filter-label">Filter:</label>
          <div className="d-flex flex-column filter-wraper">
            <Calendar
              className="input-shadow filter-calender"
              id="icon"
              name="dateFilter"
              selectionMode="range"
              value={filter?.dateFilter}
              onChange={filterOnChange}
              showIcon
              placeholder="Date"
            />
          </div>
          {!caseView && (
            <>
              <div className="d-flex flex-column filter-wraper">
                <Dropdown
                  placeholder="By Account"
                  name="account_id"
                  options={accountList}
                  value={filter?.account_id}
                  onChange={filterOnChange}
                  optionLabel={'account_name'}
                  optionValue="account_id"
                  className="w-100"
                  filter
                  showClear
                  panelFooterTemplate={isAccountsLoading ? LoaderComponent : null}
                />
              </div>
              <div className="d-flex flex-column filter-wraper">
                <MultiSelect
                  placeholder="Source"
                  name="source_account_id"
                  options={trustOptionswithExternal}
                  value={filter?.source_account_id}
                  onChange={filterOnChange}
                  optionLabel={'label'}
                  optionValue="value"
                  className="w-100"
                  filter
                  showClear
                  panelFooterTemplate={isTrustAccountsLoading ? LoaderComponent : null}
                />
              </div>
              <div className="d-flex flex-column filter-wraper">
                <MultiSelect
                  placeholder="Destination"
                  name="destination_account_id"
                  options={trustOptionswithExternal}
                  value={filter?.destination_account_id}
                  onChange={filterOnChange}
                  optionLabel={'label'}
                  optionValue="value"
                  className="w-100"
                  filter
                  showClear
                  panelFooterTemplate={isTrustAccountsLoading ? LoaderComponent : null}
                />
              </div>
            </>
          )}
          <div className="d-flex flex-column mx-2">
            <div className="pointer ms-0">
              <i className="fas fa-sync-alt" onClick={refreshData}></i>
              <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
            </div>
          </div>
        </div>
      </div>

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={operatingLogs}
          className="p-datatable-responsive"
          rowClassName={rowClassName}
          paginatorTemplate={paginatorTemplate}
          paginator
          storageKey="operating_accounting_column_widths"
          columns={[
            { field: 'case_name', header: 'Case Name', body: caseTemplate, bodyClassName: 'ellipsis-text', hidden: caseView },
            { field: 'record_created_date', header: 'Date of Transaction', body: dateBody, bodyClassName: 'ellipsis-text' },
            { field: 'debit_account_name', header: 'Paid From', body: paidFromBody, bodyClassName: 'ellipsis-text' },
            { field: 'credit_account_name', header: 'Paid To', body: accountBody, bodyClassName: 'ellipsis-text' },
            { field: 'payment_type', header: 'Payment Type', body: paymentTypeBody, bodyClassName: 'ellipsis-text' },
            { field: 'amount', header: 'Amount', body: fromTrustBody, bodyClassName: 'ellipsis-text' },
          ]}
        />
      </div>
    </>
  );
};

export default OperatingAccounting;
